import { Variant } from '@mui/material/styles/createTypography';
import { BoxComponent, BoxComponentRef } from '../../../base/box/Box';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { InlineTextStyles, RichTextContent, RichTextSegment } from '../../Commons/common.types';
import { hexToRgba } from '../../Commons';

interface IEditableContentProps {
  previewMode?: boolean;
  builderMode?: boolean;
  isDesktop?: boolean;
  text: RichTextContent;
  setComponentData?: (key: string, value: any) => void;
  dataKey: string;
  sx: any;
  getCurrentActiveState?: (value: boolean) => void;
  Element?: Variant;
  readMoreConfig?: {
    toggle: boolean;
    charLimit: number;
    lineLimit: number;
  };
  random?: string;
  readMoreButtonColor?: string;
  textAlignment?: 'left' | 'center' | 'right';
  shouldUsePrimaryHeading?: boolean;
  componentId?: any;
  backgroundColor?: string;
  onTextSelect?: (
    data: {
      text: string;
      range: { start: number; end: number };
      currentStyles?: InlineTextStyles;
      dataKey: string;
    } | null
  ) => void;
}

// Helper for HTML operations (reused to prevent GC pressure)
let helperDiv: HTMLDivElement | null = null;
const getHelperDiv = () => {
  if (typeof document === 'undefined') return null;
  if (!helperDiv) helperDiv = document.createElement('div');
  return helperDiv;
};

// Helper function to convert CSS object to style string
const cssObjectToString = (styles: Record<string, any>): string => {
  return Object.entries(styles)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      // Convert camelCase to kebab-case
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}:${value}`;
    })
    .join(';');
};

// Map MUI Typography variants to HTML tags
const variantToHtmlTag = (variant: Variant): any => {
  const mapping: Record<string, keyof JSX.IntrinsicElements> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    subtitle1: 'h6',
    subtitle2: 'h6',
    body1: 'p',
    body2: 'p',
    caption: 'span',
    button: 'span',
    overline: 'span',
  };
  return mapping[variant] || 'p';
};

// Character-based caret helpers for contentEditable with nested nodes
const getCaretOffset = (container: HTMLElement) => {
  if (typeof window === 'undefined') return 0;
  try {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(container);
    if (!container.contains(range.startContainer)) return 0;
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    return preCaretRange.toString().length;
  } catch (e) {
    return 0;
  }
};

const setCaretOffset = (container: HTMLElement, offset: number) => {
  if (typeof window === 'undefined' || !container) return;
  try {
    const selection = window.getSelection();
    const range = document.createRange();
    let currentOffset = 0;
    const traverse = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.length || 0;
        if (currentOffset <= offset && offset <= currentOffset + textLength) {
          range.setStart(node, offset - currentOffset);
          range.collapse(true);
          return true;
        }
        currentOffset += textLength;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          if (traverse(node.childNodes[i])) return true;
        }
      }
      return false;
    };
    const found = traverse(container);
    if (!found) {
      range.selectNodeContents(container);
      range.collapse(false);
    }
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  } catch (e) {
    console.error('Error setting caret offset', e);
  }
};

function EditableContent({
  previewMode = false,
  builderMode = false,
  isDesktop = true,
  setComponentData,
  dataKey,
  text,
  getCurrentActiveState,
  sx,
  Element = 'body1',
  readMoreConfig,
  readMoreButtonColor,
  shouldUsePrimaryHeading = false,
  textAlignment = 'center',
  random,
  componentId,
  backgroundColor,
  onTextSelect,
}: IEditableContentProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);
  const focusTimeoutRef = useRef<any>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Get the HTML tag from the Element variant
  const HtmlTag = shouldUsePrimaryHeading ? 'h1' : variantToHtmlTag(Element);
  const inlineTextStylesMap = sx?.inlineText || {};

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const getTextContent = useCallback((content: RichTextContent): string => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) return content.map((s) => s.text).join('');
    return '';
  }, []);

  const textString = useMemo(() => getTextContent(text), [text, getTextContent]);
  const isEmpty = !(textString && textString.trim());

  const shouldTruncate = useMemo(() => {
    if (!readMoreConfig?.toggle || !textString || isEditing) {
      return false;
    }
    return textString.length > (readMoreConfig.charLimit || 400);
  }, [readMoreConfig, textString, isEditing]);

  const formatStrokeWidth = (value: string | number | undefined) => {
    if (value === undefined || value === null || value === '') return '1px';
    if (typeof value === 'number') return `${value}px`;
    const str = String(value).trim();
    return /^-?\d+(\.\d+)?$/.test(str) ? `${str}px` : str;
  };

  const createEffectStyles = useCallback(
    (
      effect: string | undefined,
      color: string | undefined,
      textStroke?: string | number,
      fillTextColor?: string,
      effectColor?: string,
      effectIntensity?: string | number,
      fontSize?: string
    ): Record<string, any> => {
      const effectSx: Record<string, any> = {};
      if (!effect) return effectSx;
      const shadowAxis = (function () {
        if (!fontSize || parseInt(fontSize) < 25) return 1;
        if (parseInt(fontSize) < 50) return 2;
        if (parseInt(fontSize) < 75) return 3;
        if (parseInt(fontSize) < 100) return 4;
        return 4;
      })();

      const shadowIntRaw = String(effectIntensity || 4).replace(/[^0-9.]/g, '');
      const shadowInt = parseFloat(shadowIntRaw) || 4;

      const alpha = Number(`0.${shadowInt}`);

      switch (effect) {
        case 'outline':
          effectSx.WebkitTextStroke = `${formatStrokeWidth(textStroke)} ${color || 'currentColor'}`;
          effectSx.WebkitTextFillColor = 'transparent';
          effectSx.color = 'transparent';
          break;
        case 'shadow':
          effectSx.textShadow = `${shadowAxis}px ${shadowAxis}px 1px ${hexToRgba(
            effectColor || color || 'currentColor',
            alpha
          )}`;
          effectSx.color = color || 'currentColor';
          effectSx.WebkitTextFillColor = color || 'currentColor';
          break;
        case 'background-fill':
          effectSx.backgroundColor = color || 'currentColor';
          effectSx.color = fillTextColor || 'transparent';

          effectSx.padding = '0.12em 0.2em';
          effectSx.display = 'inline-block';
          effectSx.verticalAlign = 'baseline';
          effectSx.boxDecorationBreak = 'clone';
          effectSx.WebkitBoxDecorationBreak = 'clone';
          effectSx.WebkitTextFillColor = fillTextColor;

          break;
        case 'background-outline-box':
          effectSx.border = `${formatStrokeWidth(effectIntensity || 1)} solid ${
            effectColor || color || 'currentColor'
          }`;
          effectSx.padding = '0.12em 0.2em';
          effectSx.color = color;
          effectSx.display = 'inline-block';
          effectSx.verticalAlign = 'baseline';
          effectSx.boxDecorationBreak = 'clone';
          effectSx.WebkitTextFillColor = color;
          effectSx.WebkitBoxDecorationBreak = 'clone';
          break;
      }
      return effectSx;
    },
    []
  );

  const getSegmentStyles = useCallback(
    (segmentId: string): Record<string, any> => {
      const styles = inlineTextStylesMap[segmentId];
      if (!styles) return {};

      const getProperty = (prop: keyof InlineTextStyles) => styles[prop] ?? sx[prop];
      const getMobileProperty = (prop: keyof InlineTextStyles) =>
        styles['media-s']?.[prop] || sx['@media (max-width: 780px)']?.[prop];

      const getMobileEffect = () => getMobileProperty('effect');
      const getMobileStroke = () => getMobileProperty('textStroke');
      const getMobileEffectIntensity = () => getMobileProperty('effectIntensity');

      const baseColor = getProperty('color') || sx.color;
      const mobileColor = styles['media-s']?.color || sx['@media (max-width: 780px)']?.color || baseColor;

      const resolvedBackgroundColor = backgroundColor ?? sx.backgroundColor;
      const baseEffectColor = getProperty('effectColor') || sx.effectColor;
      const desktopEffectStyles = createEffectStyles(
        getProperty('effect'),
        baseColor,
        getProperty('textStroke'),
        resolvedBackgroundColor,
        baseEffectColor,
        getProperty('effectIntensity'),
        getProperty('fontSize')
      );
      const effectiveMobileEffect = getMobileEffect() || getProperty('effect');
      const mobileEffectStyles = effectiveMobileEffect
        ? createEffectStyles(
            effectiveMobileEffect,
            mobileColor,
            getMobileStroke() || getProperty('textStroke'),
            effectiveMobileEffect === 'background-fill' ? resolvedBackgroundColor : undefined,
            baseEffectColor,
            getMobileEffectIntensity() || getProperty('effectIntensity'),
            getProperty('fontSize')
          )
        : {};

      const resetParentEffects = getProperty('effect')
        ? { WebkitTextStroke: 'initial', textShadow: 'initial', backgroundColor: 'initial', border: 'initial' }
        : {};

      const baseStyles: Record<string, any> = {
        ...resetParentEffects,
        color: getProperty('color'),
        fontWeight: getProperty('fontWeight'),
        fontStyle: getProperty('fontStyle'),
        textDecoration: getProperty('textDecoration'),
        fontSize: getProperty('fontSize'),
        letterSpacing: getProperty('letterSpacing'),
        lineHeight: getProperty('lineHeight'),
        ...desktopEffectStyles,
      };

      const resetParentMobileEffects = getMobileEffect()
        ? { WebkitTextStroke: 'initial', textShadow: 'initial', backgroundColor: 'initial', border: 'initial' }
        : {};

      const mobileStyles: Record<string, any> = {
        ...resetParentMobileEffects,
        fontWeight: getMobileProperty('fontWeight'),
        fontStyle: getMobileProperty('fontStyle'),
        textDecoration: getMobileProperty('textDecoration'),
        fontSize: getMobileProperty('fontSize'),
        letterSpacing: getMobileProperty('letterSpacing'),
        lineHeight: getMobileProperty('lineHeight'),
        ...mobileEffectStyles,
      };

      if (isDesktop) {
        return { ...baseStyles, '@media (max-width: 780px)': mobileStyles };
      }

      return { ...baseStyles, ...mobileStyles, '@media (max-width: 780px)': mobileStyles };
    },
    [inlineTextStylesMap, sx, backgroundColor, createEffectStyles, isDesktop]
  );

  const truncatedContent = useMemo((): RichTextContent => {
    if (isEditing) return text;
    if (!shouldTruncate || isExpanded || !readMoreConfig) return text;

    const limit = readMoreConfig.charLimit || 400;

    if (typeof text === 'string') {
      if (text.length <= limit) return text;
      let truncatedText = text.substring(0, limit);
      const lastSpaceIndex = truncatedText.lastIndexOf(' ');
      if (lastSpaceIndex > 0) truncatedText = truncatedText.substring(0, lastSpaceIndex);
      return truncatedText + '...';
    } else {
      let currentLength = 0;
      const truncatedSegments: RichTextSegment[] = [];
      for (const segment of text) {
        if (currentLength >= limit) break;
        const remaining = limit - currentLength;
        if (segment.text.length <= remaining) {
          truncatedSegments.push(segment);
          currentLength += segment.text.length;
        } else {
          let subText = segment.text.substring(0, remaining);
          const lastSpaceIndex = subText.lastIndexOf(' ');
          if (lastSpaceIndex > 0) subText = subText.substring(0, lastSpaceIndex);
          truncatedSegments.push({ ...segment, text: subText + '...' });
          currentLength += subText.length;
          break;
        }
      }
      return truncatedSegments;
    }
  }, [shouldTruncate, isExpanded, readMoreConfig, text, isEditing]);

  // Render content as React elements (SSR-compatible)
  const renderContent = useCallback(
    (content: RichTextContent) => {
      if (typeof content === 'string') {
        return content;
      } else if (Array.isArray(content)) {
        return content.map((segment) => {
          const styles = getSegmentStyles(segment.id);
          const baseStyles = { ...styles };
          delete baseStyles['@media (max-width: 780px)'];

          return (
            <span key={segment.id} data-id={segment.id} style={baseStyles}>
              {segment.text}
            </span>
          );
        });
      }
      return null;
    },
    [getSegmentStyles]
  );

  useEffect(() => {
    if (getCurrentActiveState) {
      getCurrentActiveState(isEditing);
    }
  }, [isEditing, dataKey]);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
    };
  }, []);

  const generateHtml = useCallback(
    (content: RichTextContent): string => {
      if (typeof content === 'string') {
        const div = getHelperDiv();
        if (!div) return content;
        div.textContent = content || '';
        return div.innerHTML;
      } else if (Array.isArray(content)) {
        return content
          .map((segment) => {
            const styles = getSegmentStyles(segment.id);
            const baseStyles = { ...styles };
            delete baseStyles['@media (max-width: 780px)'];
            const styleString = cssObjectToString(baseStyles);
            const div = getHelperDiv();
            if (div) {
              div.textContent = segment.text;
              const escapedText = div.innerHTML;
              return `<span data-id="${segment.id}" style="${styleString}">${escapedText}</span>`;
            }
            return '';
          })
          .join('');
      }
      return '';
    },
    [getSegmentStyles]
  );

  // Sync content to DOM when not editing (client-side only)
  useEffect(() => {
    if (!contentRef.current || !isMounted || !builderMode) return;

    if (!isEditing && !isUpdatingRef.current) {
      if (isEmpty) {
        if (contentRef.current.innerHTML !== '') {
          contentRef.current.innerHTML = '';
        }
        return;
      }
      const html = generateHtml(truncatedContent);
      if (contentRef.current.innerHTML !== html) {
        contentRef.current.innerHTML = html;
      }
    }
  }, [isEditing, truncatedContent, generateHtml, isMounted, builderMode, isEmpty]);

  const startEditing = useCallback(() => {
    if (!previewMode && builderMode && !isEditing) {
      const initialOffset = contentRef.current ? getCaretOffset(contentRef.current) : 0;

      flushSync(() => {
        if (shouldTruncate && !isExpanded) setIsExpanded(true);
        setIsEditing(true);
        isUpdatingRef.current = true;
      });

      if (contentRef.current) {
        const fullHtml = generateHtml(text);
        if (contentRef.current.innerHTML !== fullHtml) {
          contentRef.current.innerHTML = fullHtml;
        }
        contentRef.current.focus();
        setCaretOffset(contentRef.current, initialOffset);
      }
      isUpdatingRef.current = false;
    }
  }, [previewMode, builderMode, isEditing, text, generateHtml, shouldTruncate, isExpanded]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!previewMode && builderMode && !isEditing) {
        e.preventDefault();
        e.stopPropagation();
        startEditing();
        if (onTextSelect && dataKey) {
          onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
        }
      }
    },
    [previewMode, builderMode, isEditing, startEditing, onTextSelect, dataKey]
  );

  const handleFocus = useCallback(() => {
    if (!previewMode && builderMode && !isEditing) {
      startEditing();
    }
  }, [previewMode, builderMode, isEditing, startEditing]);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!previewMode && builderMode && isEditing) {
        setIsEditing(false);
        isUpdatingRef.current = true;

        if (typeof setComponentData === 'function') {
          const container = e.currentTarget;
          const newText = container.innerText;
          // Only remove potential "Read More" button text if it leaked into innerText
          const processedText = newText.replace(/(Read More|Read Less)$/i, '');

          if (Array.isArray(text)) {
            const newSegments: RichTextSegment[] = [];

            const traverseNodes = (parent: Node) => {
              for (const node of Array.from(parent.childNodes)) {
                if (node.nodeType === Node.TEXT_NODE) {
                  const txt = node.textContent || '';
                  if (txt) {
                    newSegments.push({
                      text: txt,
                      id: `segment-${Date.now()}-${Math.random()}`,
                      styles: {},
                    });
                  }
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                  const el = node as HTMLElement;
                  if (el.tagName === 'SPAN' && el.getAttribute('data-id')) {
                    const segmentId = el.getAttribute('data-id')!;
                    const segmentText = el.innerText;
                    if (segmentText) {
                      const original = text.find((s) => s.id === segmentId);
                      newSegments.push({
                        ...(original || { id: segmentId, styles: {} }),
                        text: segmentText,
                      });
                    }
                  } else if (el.tagName === 'BR') {
                    newSegments.push({
                      text: '\n',
                      id: `segment-${Date.now()}-${Math.random()}`,
                      styles: {},
                    });
                  } else if (el.tagName === 'DIV' || el.tagName === 'P') {
                    // Block elements naturally imply a newline if there's preceding content
                    if (newSegments.length > 0 && newSegments[newSegments.length - 1].text !== '\n') {
                      newSegments.push({
                        text: '\n',
                        id: `segment-${Date.now()}-${Math.random()}`,
                        styles: {},
                      });
                    }
                    traverseNodes(el);
                    // Also potentially add a newline after if not at the absolute end
                  } else {
                    traverseNodes(el);
                  }
                }
              }
            };

            traverseNodes(container);

            const filteredSegments = newSegments.filter((segment) => segment.text.trim().length > 0);
            const trimmedProcessed = processedText.trim();
            if (filteredSegments.length === 0 && trimmedProcessed.length > 0) {
              filteredSegments.push({
                text: trimmedProcessed,
                id: `segment-${Date.now()}-${Math.random()}`,
                styles: {},
              });
            }
            setComponentData(
              dataKey,
              filteredSegments.length === 0 ? (typeof text === 'string' ? '' : []) : filteredSegments
            );
          } else {
            setComponentData(dataKey, processedText.trim());
          }
        }

        isUpdatingRef.current = false;
      }
    },
    [previewMode, builderMode, isEditing, text, setComponentData, dataKey]
  );

  const checkSelection = useCallback(() => {
    if (!onTextSelect || !builderMode || previewMode || !contentRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      if (selection?.isCollapsed) onTextSelect(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const container = contentRef.current;
    if (!container || !container.contains(range.commonAncestorContainer)) return;

    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(container);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const start = preCaretRange.toString().length;
    const end = start + range.toString().length;

    let currentStyles: InlineTextStyles = {};
    let node: Node | null = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;

    if (node && node.nodeName === 'SPAN') {
      const span = node as HTMLElement;
      const segmentId = span.getAttribute('data-id');
      if (segmentId && inlineTextStylesMap[segmentId]) {
        currentStyles = inlineTextStylesMap[segmentId];
      }
    }

    onTextSelect({ text: selection.toString(), range: { start, end }, currentStyles, dataKey });
  }, [onTextSelect, builderMode, previewMode, inlineTextStylesMap, dataKey]);

  const showReadMoreButton = readMoreConfig?.toggle && shouldTruncate;

  const getWrapperStyles = useCallback(() => {
    const wrapperSx = { ...sx };
    delete wrapperSx.inlineText;
    if (wrapperSx.effect) {
      const effectStyles = createEffectStyles(
        wrapperSx.effect,
        wrapperSx.color,
        wrapperSx.textStroke,
        backgroundColor ?? wrapperSx.backgroundColor,
        wrapperSx.effectColor,
        wrapperSx.effectIntensity,
        wrapperSx.fontSize
      );
      Object.assign(wrapperSx, effectStyles);
    }
    return wrapperSx;
  }, [sx, createEffectStyles, backgroundColor]);

  const wrapperStyles = getWrapperStyles();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const styleId = `editable-content-mobile-${componentId || dataKey}`;
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    let css = '';
    const wrapperSelector = `[data-attr-editable-id="${componentId || dataKey}"]`;
    const effectiveMobileWrapperEffect = sx?.['@media (max-width: 780px)']?.effect || sx?.effect;

    if (effectiveMobileWrapperEffect) {
      const baseColor = sx?.color;
      const mobileColor = sx?.['@media (max-width: 780px)']?.color || baseColor;
      const mobileEffectStyles = createEffectStyles(
        effectiveMobileWrapperEffect,
        mobileColor,
        sx?.['@media (max-width: 780px)']?.textStroke || sx?.textStroke,
        effectiveMobileWrapperEffect === 'background-fill' ? backgroundColor ?? sx?.backgroundColor : undefined,
        sx?.effectColor,
        sx?.['@media (max-width: 780px)']?.effectIntensity || sx?.effectIntensity,
        sx?.fontSize
      );
      if (Object.keys(mobileEffectStyles).length > 0) {
        css += `@media (max-width: 780px) { ${wrapperSelector} { ${cssObjectToString(mobileEffectStyles)} } }`;
      }
    }

    if (Array.isArray(text)) {
      text.forEach((segment) => {
        const styles = getSegmentStyles(segment.id);
        const mobileStyles = styles['@media (max-width: 780px)'];
        if (mobileStyles) {
          css += `@media (max-width: 780px) { [data-id="${segment.id}"] { ${cssObjectToString(mobileStyles)} } }`;
        }
      });
    }

    css += `
      ${wrapperSelector}::selection,
      ${wrapperSelector} *::selection {
        background-color: rgba(0, 130, 121, 0.3) !important;
        color: inherit !important;
      }
      ${wrapperSelector}::-moz-selection,
      ${wrapperSelector} *::-moz-selection {
        background-color: rgba(0, 130, 121, 0.3) !important;
        color: inherit !important;
      }
    `;

    styleElement.innerHTML = css;
    return () => {
      if (styleElement && document.head.contains(styleElement)) document.head.removeChild(styleElement);
    };
  }, [text, componentId, dataKey, getSegmentStyles, sx, createEffectStyles, backgroundColor]);

  return (
    <BoxComponentRef
      sx={{
        textAlign: textAlignment,
        border: isEditing ? '2px solid rgb(0, 130, 121)' : '2px solid transparent',
        outline: 0,
        position: 'relative',
        display: 'block',
        width: '100%',
      }}
      onMouseUp={builderMode ? checkSelection : undefined}
      onKeyUp={builderMode ? checkSelection : undefined}
    >
      {builderMode && (
        <style>
          {`
            [data-attr-editable-id="${componentId || dataKey}"][data-placeholder]:empty:before {
              content: attr(data-placeholder);
              color: #999;
              font-style: italic;
              pointer-events: none;
            }
            [data-attr-editable-id="${componentId || dataKey}"]::selection,
            [data-attr-editable-id="${componentId || dataKey}"] *::selection {
              background-color: rgba(0, 130, 121, 0.45) !important;
              color: inherit !important;
              -webkit-text-fill-color: initial !important;
              text-fill-color: initial !important;
            }
          `}
        </style>
      )}
      <HtmlTag
        ref={contentRef as any}
        data-attr-editable-id={componentId || dataKey}
        data-placeholder={builderMode && isEmpty ? 'Start typing here...' : undefined}
        style={{
          marginTop: '0',
          marginBottom: '0',
          ...wrapperStyles,
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          outline: 'none',
          textAlign: textAlignment,
          display: Element === 'body1' ? 'inline' : 'block',
          fontFamily: sx?.fontFamily,
          fontSize: sx?.fontSize,
          fontWeight: sx?.fontWeight,
          lineHeight: sx?.lineHeight,
          letterSpacing: sx?.letterSpacing,
          cursor: builderMode ? (isEditing ? 'text' : 'pointer') : 'default',
          caretColor: 'auto',
          userSelect: builderMode ? 'text' : 'inherit',
          WebkitUserSelect: builderMode ? 'text' : 'inherit',
        }}
        spellCheck={false}
        contentEditable={!previewMode && builderMode}
        suppressContentEditableWarning={true}
        onMouseDown={builderMode ? handleMouseDown : undefined}
        onFocus={builderMode ? handleFocus : undefined}
        onBlur={builderMode ? handleBlur : undefined}
      >
        {/* Only render React children during SSR or in preview mode (not builder mode). When empty in builder, render nothing so :empty placeholder shows. */}
        {!isEditing && (!isMounted || !builderMode) && (!isEmpty ? renderContent(truncatedContent) : null)}
      </HtmlTag>
      {showReadMoreButton && (
        <BoxComponent
          component="span"
          onClick={() => setIsExpanded(!isExpanded)}
          contentEditable={false}
          sx={{
            cursor: 'pointer',
            fontWeight: 600,
            textDecoration: 'none',
            display: 'inline',
            color: readMoreButtonColor || sx?.color,
            fontSize: sx?.fontSize,
            fontFamily: sx?.fontFamily,
            lineHeight: sx?.lineHeight,
            marginLeft: '4px',
            userSelect: 'none',
            pointerEvents: 'auto',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </BoxComponent>
      )}
    </BoxComponentRef>
  );
}

export default EditableContent;
